<script setup lang="ts">
interface Service {
  name: string;
  href: string;
}

interface SolutionPillar {
  id: string;
  icon: string;
  title: string;
  tagline: string;
  painPoint: string;
  services: Service[];
  ctaText: string;
  ctaHref: string;
}

const pillars: SolutionPillar[] = [
  {
    id: "connectivity",
    icon: "lucide:wifi",
    title: "Connectivity",
    tagline: "Keep your business online and talking.",
    painPoint: "Stop suffering from dropouts, dead zones, and slow speeds.",
    services: [
      { name: "Business NBN & Fibre", href: "/internet-data/business-nbn" },
      { name: "Enterprise Ethernet", href: "/internet-data/enterprise-ethernet" },
      { name: "VoIP Phone Systems", href: "/voice-video/voip-phone" },
      { name: "Microsoft Teams Voice", href: "/voice-video/microsoft-teams-voice" },
      { name: "WiFi Solutions", href: "/project-services/wifi-solutions" },
    ],
    ctaText: "Explore Connectivity",
    ctaHref: "/internet-data",
  },
  {
    id: "security",
    icon: "lucide:shield-check",
    title: "Security",
    tagline: "See, control and protect every site.",
    painPoint: "Eliminate blind spots and manage access from anywhere.",
    services: [
      { name: "CCTV Systems", href: "/security-services/cctv" },
      { name: "Access Control", href: "/security-services/access-control" },
      { name: "Alarm Systems", href: "/security-services/alarm-systems" },
      {
        name: "24/7 Monitoring",
        href: "/security-services/back-to-base-monitoring-24-7",
      },
      { name: "Lone Worker Safety", href: "/security-services/lone-worker-solutions" },
    ],
    ctaText: "Explore Security",
    ctaHref: "/security-services",
  },
  {
    id: "itcloud",
    icon: "lucide:cloud",
    title: "IT & Cloud",
    tagline: "IT that prevents fires, rather than just fighting them.",
    painPoint: 'Move from reactive "break/fix" to proactive growth.',
    services: [
      { name: "Managed Service Desk", href: "/managed-it-services" },
      { name: "Remote Monitoring", href: "/managed-it-services/remote-monitoring-management" },
      { name: "Cloud Backup & DR", href: "/cloud-services/cloud-backup" },
      { name: "Private Cloud", href: "/cloud-services/private-cloud" },
      { name: "Cyber Security", href: "/managed-it-services/online-and-system-security" },
    ],
    ctaText: "Explore IT & Cloud",
    ctaHref: "/managed-it-services",
  },
];
</script>

<template>
  <section class="bg-neutral-50 py-20 lg:py-24">
    <div class="mx-auto max-w-7xl px-8">
      <!-- Section header -->
      <div class="text-center">
        <p class="text-sm font-semibold uppercase tracking-wider text-primary-400">
          What We Do
        </p>
        <h2
          class="mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl"
        >
          Integrated Solutions. One Accountable Partner.
        </h2>
        <p class="mx-auto mt-4 max-w-2xl text-lg leading-7 text-neutral-600">
          Three core capabilities, seamlessly integrated to keep your business running
          without the vendor blame game.
        </p>
      </div>

      <!-- Solution pillars grid -->
      <div class="mt-16 grid gap-8 lg:grid-cols-3">
        <div
          v-for="pillar in pillars"
          :id="pillar.id"
          :key="pillar.id"
          class="group relative flex flex-col rounded-2xl border border-neutral-200 bg-white p-8 shadow-xs transition-all duration-300 hover:-translate-y-1"
        >
          <!-- Icon (Hero style - large, standalone, with hover scale) -->
          <div class="mb-6">
            <Icon
              :name="pillar.icon"
              size="48"
              class="text-mw-accent-500 transition-transform duration-300 group-hover:scale-110"
            />
          </div>

          <!-- Title & tagline -->
          <h3 class="text-2xl font-extrabold text-dark">
            {{ pillar.title }}
          </h3>
          <p class="mt-2 min-h-14 text-lg leading-7 text-neutral-600">
            {{ pillar.tagline }}
          </p>

          <!-- Pain point callout -->
          <div class="mt-4 rounded-lg bg-neutral-50 p-4">
            <p class="text-sm text-neutral-600">
              <span class="font-bold text-slate-900">We help when:</span>
              {{ pillar.painPoint }}
            </p>
          </div>

          <!-- Services list -->
          <ul class="mt-6 flex-1 space-y-3">
            <li v-for="service in pillar.services" :key="service.name">
              <NuxtLink
                :to="service.href"
                class="group/link inline-flex items-center gap-2 text-neutral-600 transition-colors hover:text-primary-400"
              >
                <Icon name="lucide:check" class="h-4 w-4 shrink-0 text-primary-400" />
                <span class="group-hover/link:underline">{{ service.name }}</span>
              </NuxtLink>
            </li>
          </ul>

          <!-- CTA (Pill-style button) -->
          <div class="mt-8 border-t border-neutral-100 pt-6">
            <NuxtLink
              :to="pillar.ctaHref"
              class="inline-flex items-center gap-2 rounded-full border border-primary-400 px-5 py-2.5 font-bold text-primary-400 transition-all hover:bg-primary-400 hover:text-white"
            >
              {{ pillar.ctaText }}
              <Icon
                name="lucide:arrow-right"
                size="20"
                class="transition-transform group-hover:translate-x-0.5"
              />
            </NuxtLink>
          </div>

          <!-- Bottom accent line (scales from left on hover) -->
          <div
            class="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 rounded-b-2xl bg-primary-400 transition-transform duration-300 group-hover:scale-x-100"
            aria-hidden="true"
          ></div>
        </div>
      </div>
    </div>
  </section>
</template>
